import { DataTableColDef } from '@chronosphereio/chrono-ui';
import type { YAXisComponentOption } from 'echarts';
import {
  ContentWithLegendProps,
  ExcludeEmptyResults,
  LegendDensity,
  LegendItem,
  SeriesActionContext,
  ShowEndTimeArgs,
  TableLegendPinnedColumns,
} from '@/components';
import {
  EventPayload,
  LegendMode,
  NullBehaviorOptions,
  SeriesMenuItemOptions,
  StackOptions,
  TimeSeriesChartPropsBase,
  TimeSeriesChartSpec,
  TimeSeriesChartVisualOptions,
  TimeSeriesChartYAxisOptions,
  TimeSeriesDataset,
  TimeSeriesWithoutValues,
  UnitOptions,
  VizLegendOptions,
} from '@/core';
import { BasePanelProps, GlobalFilter, OptionsEditorProps, PropToSpecMapping } from '../../../plugin-system';
import { UseProcessTimeSeriesDatasetProps } from './hooks';
import { UseTimeSeriesGroupByProps } from './hooks/use-time-series-group-by';

export type XAxisTimeRange = 'time-range-context' | 'data';

/**
 * Types for the time series chart that are only used in the `Viz` variant.
 *
 * @see {@link TimeSeriesChartPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface TimeSeriesChartPropsVizOnly
  extends Pick<ContentWithLegendProps, 'enableConsistentLegendSize' | 'collapsibleLegendProps'> {
  // We currently handle the legend differently in the `Viz` use case.
  legend?: VizLegendOptions;

  /**
   * Configuration for customizing the tooltip context menu
   */
  tooltipContextMenu?: TooltipContextMenuOptions;

  /**
   * If true, display the end time in tooltip.
   * This helps user understand the time frame represented by the bar they are viewing within a time series histogram.
   */
  showTooltipEndTime?: ShowEndTimeArgs;

  /**
   * If true, display the total value of all series in tootlip.
   */
  showTooltipTotal?: boolean;

  /**
   * If true, empty results (null/zero) will be hidden in the tooltip.
   */
  excludeEmptyResults?: ExcludeEmptyResults;

  /**
   * Controls how many series show in the tooltip, this is inclusive so series shown will never be more than this number.
   * By default, 10 nearby series will show, this could be adjusted depending on the size of a chart.
   * Finding the right balance also depends on performance testing for individual embed use cases.
   */
  maxNearbySeries?: number;

  /**
   * Whether to shorten values in the tooltip. When enabled, the full value will always be shown (ex: 1,000,000 instead of 1M).
   * This allows the tooltip to be more precise than the yAxis.
   * @default true
   */
  enableTooltipPreciseValues?: boolean;

  /**
   * Whether to disable showing common labels separately in the nearby mode tooltip.
   * @default false
   */
  disableDeclutterCommonLabels?: boolean;

  /**
   * Series name is truncated at one line in the tooltip by default, this property can be used to wrap to multiple lines.
   * @default false
   */
  wrapSeriesName?: boolean;

  /**
   * Click handler for selected time series
   */
  onSeriesClick?: (e: ClickSeriesData) => void;

  /**
   * Click handler for selected event
   */
  onEventClick?: (e: EventPayload) => void;

  /**
   * Controls the horizontal spacing between bars in a bar chart.
   */
  barCategoryGap?: number;

  /**
   * Gives the ability to render a custom action for each series in the tooltip.
   */
  renderSeriesActionTooltip?: (series: TimeSeriesWithoutValues, ctx: SeriesActionContext) => React.ReactNode;

  /**
   * When true and configured to show a bottom-positioned legend, the time series chart will include a text input that
   * provides a quick, text-based filter of the series shown in both the chart and the legend.
   */
  showQuickFilter?: boolean;

  /**
   * When true and configured to show a bottom-positioned legend, the time series chart will include a button group
   * that enables the user to toggle between List and Table legend modes.
   */
  showToggleMode?: boolean;

  /**
   * Callback that is called when the user changes the mode using the toggle that is shown when `showToggleMode` is true.
   * May be useful in some cases (e.g. saving the user's last selected mode in local storage on the monitors page).
   *
   * Strongly recommend wrapping functions passed to this prop in `useCallback` to minimize potential performance issues.
   *
   * Note that the mode is uncontrolled when using `showQuickFilter` (i.e. the legend state will be initialized based on
   * props, but changes to the state will be managed internally), so this callback primarily exists for handling side
   * effects to the mode changing. Please reach out for help at https://go/vf-help if you have a use case that requires
   * a controlled mode with the toggle.
   */
  onChangeMode?: (newMode: LegendMode) => void;

  /**
   * When true and the time series chart is rendering a table legend, the labels will be rendered as columns.
   *
   * If you only want to show a subset of the labels, use the `filterLabelColumns` prop to filter out any unwanted
   * labels.
   */
  enableLabelsToColumns?: boolean;

  /**
   * Custom function that can be used alongside `enableLabelsToColumns` to filter the labels that are shown to the
   * user. Can be useful in product experiences where you only want to show a subset of critical labels.
   *
   * Ensure that this function is memoized (e.g. using `useCallback`, defining outside the render cycle) to avoid
   * performance issues.
   */
  filterLabelColumns?: (labelName: string) => boolean;

  /**
   * Disables truncation of the list legend items, allowing them to wrap to the next line.
   * Useful when there is enough space, and users may need to view or copy the full legend item name.
   */
  disableListLegendItemTruncation?: boolean;

  /**
   * When series include icons, the table legend will show a column with those icons. By default it has an empty
   * header name. Set this prop to use a more specific name in the header.
   */
  iconColumnHeaderName?: string;

  /**
   * Determines the density of the legend in Table mode. Primarily impacts the height of rows.
   * @default 'compact'
   */
  legendDensity?: LegendDensity;

  /**
   * Enables the chrono ui DataTable menu that appears on hover over the column header in the table legend.
   * @default true
   */
  enableTableLegendColumnMenu?: boolean;

  /**
   * Array of custom columns to be included at the start of the table legend, to the right of the
   * checkbox and icon columns, and to the left of the name column (if it is used). If you want to render data that is
   * not passed to the legend by default, use `getCustomLegendItemData`.
   *
   * If you would like to enable add/remove filter buttons to label columns, also set the `onAddFilters` prop.
   *
   * Ensure this prop is memoized (e.g. using `useMemo`) to avoid performance issues.
   */
  customTableLegendColumnsStart?: Array<DataTableColDef<LegendItem>>;

  /**
   * Array of custom columns to be included at the end of the table legend. If you want to render data that is
   * not passed to the legend by default, use `getCustomLegendItemData`.
   *
   * If you would like to enable add/remove filter buttons to label columns, also set the `onAddFilters` prop.
   *
   * Ensure this prop is memoized (e.g. using `useMemo`) to avoid performance issues.
   */
  customTableLegendColumnsEnd?: Array<DataTableColDef<LegendItem>>;

  /**
   * Set this value to customize the pinned table legend columns. It may be set to one of the following:
   * - `'auto'`: Will automatically determine which columns to pin based on other chart configuration. This setting
   *    currently pins all columns associated with the `legend.values` configuration to the right when they are present
   *    and `enableLabelsToColumns` is true. It also pins the checkbox column to the left.
   * - `TableLegendPinnedColumns`: Full customized pinned columns configuration that may be useful when doing more
   *   significant table customization using `customTableLegendColumnsStart` or `customTableLegendColumnsEnd`.
   * - `'none''`: Disables the default 'auto` mode. Only the checkbox column will be pinned.
   *
   * Additional things to keep in mind with this feature:
   * - The checkbox column is always configured to be pinned to the left and this cannot be modified.
   * - Column pinning attempts to be responsive when possible. This is handled in the following ways:
   *   - If all columns can be viewed without horizontal scrolling, columns are not pinned because the
   *     feature is not needed.
   *   - If pinning is likely to put the user in a bad state because the chart is too narrow, pinning will be disabled.
   *     This logic depends on evaluation of columns with an explicit numeric `width` property, so may not work well
   *     for pinning of dynamic width columns.
   *
   * @default 'auto'
   */
  pinnedTableLegendColumns?: 'auto' | 'none' | TableLegendPinnedColumns;

  /**
   * Function that returns additional data that will get passed to the legend for rendering.
   * Used alongside `customTableLegendColumnsStart` or `customTableLegendColumnsEnd` to render data that is not passed to the legend by
   * default.
   *
   * Ensure that this function is memoized (e.g. using `useCallback`) to avoid performance issues.
   */
  getCustomLegendItemData?: (timeSeries: TimeSeriesDataset) => LegendItem['data'];

  /**
   * Callback of filters to be added. When this is provided and `enableLabelsToColumns` is true, label columns will
   * render global filter buttons on hover.
   */
  onAddFilters?: (filters: GlobalFilter[]) => void;

  /**
   * The id of the series to focus/drilldown.
   */
  focusedSeriesId?: string;

  /**
   * Enables sorting of series by total value when visual.stack is set.
   * This ensures that series with a higher total appear at the bottom of the stack.
   * Set to false to preserve original query order.
   * @default true
   */
  enableStackSort?: boolean;

  /**
   * Configuration for enabling the "group by" feature. Note that this feature currently only supports
   * "controlled" use cases, so you are responsible for managing the state.
   */
  groupByProps?: TimeSeriesGroupByProps;

  /**
   * Controls how the x-axis time range is determined.
   * - 'time-range-context': Uses the time range from the time range context.
   *   This is the default and appropriate for most use cases where you want the chart to respect
   *   the user's selected time range, even if data extends beyond it.
   * - 'data': Auto-fits to the data's time range. Use this when:
   *   - The data's actual time range extends beyond the time picker range and you want to show
   *     the complete data extent
   *   - You're displaying aggregated data (daily, weekly, etc.) where data points may span
   *     boundaries that extend outside the selected time range
   *
   * @default 'time-range-context'
   */
  xAxisTimeRange?: XAxisTimeRange;

  /**
   * Override the sync group used for shared crosshair and drag-to-zoom interactions.
   * By default, the sync group is derived from the app metadata context. Set this prop
   * to use a custom group, or set to `false` to opt out of syncing entirely.
   */
  syncGroup?: string | false;

  /**
   * When set, renders a dashed vertical marker line at the given timestamp (epoch ms)
   * on the chart's x-axis. Useful for indicating a scroll position or point of interest
   * that maps to a time value elsewhere in the UI.
   */
  scrollMarkerTimestampMs?: number | null;
}

export interface TimeSeriesChartProps extends BasePanelProps, TimeSeriesChartPropsBase, TimeSeriesChartPropsVizOnly {
  // Extend values from `TimeSeriesChartPropsBase` below, as needed, to add in jsdoc to help developers.
}

export interface ClickSeriesData {
  /**
   * The series clicked
   */
  series: TimeSeriesWithoutValues;
  /**
   * The start timestamp of the data point clicked
   */
  startMs?: number;
  /**
   * The end timestamp of the data point clicked
   */
  endMs?: number;
  /**
   * The value of the data point clicked
   */
  value?: number | null;
}

export interface TimeSeriesGroupByProps
  extends Omit<Required<UseTimeSeriesGroupByProps>, 'hasTimeSeriesGrouping'>,
    Pick<UseProcessTimeSeriesDatasetProps, 'getGroupKey'> {
  /**
   * Required function for using the "group by" feature. It takes an array of legend items associated
   * with a given group (i.e. the items associated with the group that are rendered when `isGrouped`
   * is false) and should return a single legend item that will be used to render the group in the
   * legend when `isGrouped` is true.
   *
   * This function can impact expensive memoized operations in the time series chart, so it is
   * recommended to be defined statically (i.e. outside the render cycle) or wrapped in `useCallback`
   * when possible to avoid performance issues.
   */
  getGroupLegendItem: (legendItems: LegendItem[]) => Partial<Omit<LegendItem, 'id' | 'color'>>;
}

interface TooltipContextMenuBase {
  isTooltipPinned: boolean;
}

export type TooltipContextMenuCtx = TooltipContextMenuBase & {
  /**
   * Optional timestamp of the tooltip, if available.
   */
  timestamp?: number;
};

type TooltipSeriesContextMenuCtx = TooltipContextMenuBase & {
  timestamp: number;
  seriesKey: string;
};

export interface TooltipContextMenuOptions {
  /**
   * Function to return custom menu items specfic to a single series
   */
  seriesMenuItems?: (ctx: TooltipSeriesContextMenuCtx, series: TimeSeriesWithoutValues) => SeriesMenuItemOptions[];
  /**
   * Function to return general custom menu items
   */
  menuItems?: (ctx: TooltipContextMenuCtx) => SeriesMenuItemOptions[];
}

export type TimeSeriesChartOptionsEditorProps = OptionsEditorProps<TimeSeriesChartSpec>;

export const DEFAULT_UNIT: UnitOptions = {
  kind: 'Decimal',
  abbreviate: true,
};

export const DEFAULT_Y_AXIS: TimeSeriesChartYAxisOptions = {
  show: true,
  label: '',
  unit: DEFAULT_UNIT,
  min: undefined,
  max: undefined,
};

export const Y_AXIS_CONFIG = {
  show: { label: 'Show' },
  label: { label: 'Label' },
  unit: { label: 'Unit' },
  min: { label: 'Min' },
  max: { label: 'Max' },
};

interface TimeSeriesYAxisOverrides {
  unit?: UnitOptions;
}

export type TimeSeriesChartYAxisComponentOption = YAXisComponentOption & {
  // We only support "value" and "log" types for the y-axis in the time series chart. Using this adjusted TS type
  // with the `type` property explicitly required AND limited to just what we support makes it much easier to work
  // with the new union type underlying YAXisComponentOption in echart v6. Without it, a bunch of types get grumpy
  // because different properties are supported and/or have different typing depending on the value of the `type`
  // property.
  type: 'value' | 'log';
};

export type TimeSeriesChartExtendedYAxisOptions = TimeSeriesChartYAxisComponentOption & TimeSeriesYAxisOverrides;

export const DEFAULT_LINE_WIDTH = 1;

export const DEFAULT_BAR_CATEGORY_GAP = 2;

export const DEFAULT_AREA_OPACITY = 0;
export const DEFAULT_THRESHOLD_AREA_OPACITY = 0;

// How much larger datapoint symbols are than line width, also applied in VisualOptionsEditor.
export const POINT_SIZE_OFFSET = 2;
export const DEFAULT_POINT_RADIUS = DEFAULT_LINE_WIDTH + POINT_SIZE_OFFSET;

export const DEFAULT_CONNECT_NULLS = false;

export const DEFAULT_VISUAL: TimeSeriesChartVisualOptions = {
  line_width: DEFAULT_LINE_WIDTH,
  area_opacity: DEFAULT_AREA_OPACITY,
};

// Controls how often static threshold values should be plotted so threshold data shows
// in tooltip without flicker.
export const THRESHOLD_PLOT_INTERVAL = 15;

export const VISUAL_CONFIG = {
  line_width: {
    label: 'Line width',
    testId: 'slider-line-width',
    min: 0.25,
    max: 3,
    step: 0.25,
  },
  area_opacity: {
    label: 'Area opacity',
    testId: 'slider-area-opacity',
    min: 0,
    max: 1,
    step: 0.05,
  },
  stack: {
    label: 'Stack series',
  },
  connect_nulls: {
    label: 'Connect nulls',
  },
  color_palette: {
    label: 'Color palette',
  },
  null_behavior: {
    label: 'Null behavior',
  },
};

export const STACK_CONFIG = {
  None: { label: 'None' },
  All: { label: 'All' },
  Percent: { label: 'Percent' },
};
export const STACK_OPTIONS = Object.entries(STACK_CONFIG).map(([id, config]) => {
  return {
    id: id as StackOptions,
    ...config,
  };
});

export const NULL_BEHAVIOR_UNDEF_LABEL_NAME = 'Default';

export const NULL_BEHAVIOR_CONFIG = {
  Default: { label: NULL_BEHAVIOR_UNDEF_LABEL_NAME },
  Connected: { label: 'Connected' },
  NullAsZero: { label: 'Null as zero' },
};
export const NULL_BEHAVIOR_OPTIONS = Object.entries(NULL_BEHAVIOR_CONFIG).map(([id, config]) => {
  return {
    id: id as NullBehaviorOptions,
    ...config,
  };
});

// Both of these constants help produce a value that is LESS THAN the initial value.
// For positive values, we multiply by a number less than 1 to get this outcome.
// For negative values, we multiply to a number greater than 1 to get this outcome.
export const POSITIVE_MIN_VALUE_MULTIPLIER = 0.8;
export const NEGATIVE_MIN_VALUE_MULTIPLIER = 1.2;

/**
 * Creates an initial/empty options object for the TimeSeriesChartPanel.
 */
export function createInitialTimeSeriesChartSpec(): TimeSeriesChartSpec {
  return {
    tooltip: {
      // TODO: migrate to PascalCase 'Single' when we have panel schema migration approach
      mode: 'single',
    },
  };
}

/**
 * Defines a mapping between a chart's props and its equivalent dashboard spec.
 * Keys represent the chart's props, while values correspond to the spec.
 */
export const TIME_SERIES_CHART_PROP_TO_SPEC_MAPPING: PropToSpecMapping<TimeSeriesChartProps, TimeSeriesChartSpec> = {
  legend: 'legend',
  yAxis: 'y_axis',
  yAxisRight: 'y_axis_right',
  thresholds: 'thresholds',
  visual: 'visual',
  tooltipMode: 'tooltip.mode',
  tooltipSort: 'tooltip.sort',
  enableTooltipPinning: 'tooltip.enable_pinning',
  overrides: 'overrides',
};
